import { MapPin, Calendar, Clock, CheckCircle, Share2, Flag } from 'lucide-react';
import { Campaign } from '../../hooks/useCampaignDetail';
import { formatDate, getDaysRemaining, isExpired } from '../../utils/date';

interface Props {
  campaign: Campaign;
  isOwner: boolean;
  onDonateClick: () => void;
  onShareClick: () => void;
  onCompleteClick?: () => void;
  onReportClick?: () => void;
}

export function CampaignDetailHeader({ campaign, isOwner, onDonateClick, onShareClick, onCompleteClick, onReportClick }: Props) {
  const daysRemaining = getDaysRemaining(campaign.deadline);
  const expired = isExpired(campaign.deadline);

  return (
    <div className="bg-white border-b border-gray-200">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
        {/* Top row: status badges + actions */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {campaign.status === 'completed' && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <CheckCircle className="w-3 h-3 mr-1" />
                Finalizada
              </span>
            )}
            {campaign.status === 'expired' && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                <Clock className="w-3 h-3 mr-1" />
                Vencida
              </span>
            )}
            {expired && campaign.status === 'active' && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                <Clock className="w-3 h-3 mr-1" />
                Expirada
              </span>
            )}
            {!expired && campaign.status === 'active' && daysRemaining <= 7 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                <Clock className="w-3 h-3 mr-1" />
                {daysRemaining} días
              </span>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={onShareClick}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Compartir"
            >
              <Share2 className="w-5 h-5" />
            </button>

            {!isOwner && onReportClick && (
              <button
                onClick={onReportClick}
                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                title="Reportar campaña"
              >
                <Flag className="w-4 h-4" />
              </button>
            )}

            {isOwner && (campaign.status === 'active' || campaign.status === 'expired') && onCompleteClick && (
              <button
                onClick={onCompleteClick}
                className="flex items-center space-x-1.5 bg-green-600 text-white hover:bg-green-700 px-3 py-2 rounded-lg font-medium transition-colors text-sm"
                title="Finalizar campaña"
              >
                <Flag className="w-4 h-4" />
                <span>Finalizar</span>
              </button>
            )}
          </div>
        </div>

        {/* Title */}
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          {campaign.title}
        </h1>

        {/* Meta info */}
        <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-gray-500">
          <div className="flex items-center">
            <MapPin className="w-3.5 h-3.5 mr-1" />
            <span>{campaign.location?.city}, {campaign.location?.state}</span>
          </div>
          <span className="text-gray-300">•</span>
          <div className="flex items-center">
            <Calendar className="w-3.5 h-3.5 mr-1" />
            <span>Puedes aportar hasta: {formatDate(campaign.deadline)}</span>
          </div>
        </div>

        {/* Days remaining message */}
        {!expired && campaign.status === 'active' && (
          <p className="mt-2 text-xs sm:text-sm text-amber-700 font-medium">
            Te quedan solo {daysRemaining} días para que puedas aportar
          </p>
        )}
      </div>
    </div>
  );
}
